/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import Preloader from '@omilia/preloader';

import { fileType, projectType } from 'types';
import UnitsSearchResult from 'components/Units/UnitsSearchResult';
import FilesAndFoldersHeader from 'components/FilesAndFoldersHeader';
import { SPagination } from 'components/styled';
import NoFiles from './components/NoFiles';
import List from './components/List';
import DeleteModal from './components/DeleteModal';
import UploadFileModal from './components/UploadFileModal';

import './style.scss';

const PAGE_SIZE = 100;

export default class Files extends Component {
  static propTypes = {
    location: PropTypes.shape({ pathname: PropTypes.string.isRequired })
      .isRequired,
    project: projectType.isRequired,
    projectIsLoading: PropTypes.bool.isRequired,
    addFile: PropTypes.func,
    addNewFile: PropTypes.func,
    count: PropTypes.number,
    currentPage: PropTypes.number,
    deleteFile: PropTypes.func,
    deleteFiles: PropTypes.func,
    exportFile: PropTypes.func,
    exportFiles: PropTypes.func,
    getFiles: PropTypes.func,
    hasNameExceptions: PropTypes.bool,
    hasUploadingFilesError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isUploading: PropTypes.bool,
    items: PropTypes.arrayOf(fileType),
    lastLocation: PropTypes.shape({ pathname: PropTypes.string.isRequired }),
    modelName: PropTypes.string,
    openFile: PropTypes.func,
    openFiles: PropTypes.func,
    resetUploadFiles: PropTypes.func,
    searchTerm: PropTypes.string,
    sidebarIsOpen: PropTypes.bool,
    toggleFilesSelection: PropTypes.func,
    updatedSearchTerm: PropTypes.func,
    updateFile: PropTypes.func,
    uploadFiles: PropTypes.func,
  };

  static defaultProps = {
    hasNameExceptions: false,
  };

  // There is also the currentPage in redux. Probably when we implement server side pagination, we will
  // use either this one or the one from redux.
  state = {
    isUploadFileModalOpen: false,
    isDeleteModalOpen: false,
    currentPage: 1,
  };

  componentDidMount() {
    this.props.getFiles({ page: this.props.currentPage });
  }

  componentWillReceiveProps(nextProps) {
    // When the user searches for a test set
    // the items are sliced so the currentPage needs to
    // be reset
    if (nextProps.searchTerm !== '') {
      this.setState({
        currentPage: 1,
      });
    }
  }

  handlePageChange = pageNumber => {
    this.setState({
      currentPage: pageNumber,
    });
  };

  handleOnOpenModal = () => {
    this.setState({
      isUploadFileModalOpen: true,
    });
  };

  handleOnCloseModal = () => {
    this.setState({
      isUploadFileModalOpen: false,
    });
  };

  handleOpenDeleteModal = () => {
    this.setState({
      isDeleteModalOpen: true,
    });
  };

  handleCloseDeleteModal = () => {
    this.setState({
      isDeleteModalOpen: false,
    });
  };

  handleDeleteFiles = async () => {
    await this.props.deleteFiles(this.props.items);
    this.handleCloseDeleteModal();
  };

  showAll = () => this.props.updatedSearchTerm('');

  renderPreloader = () => {
    const { isLoading, projectIsLoading } = this.props;

    if (isLoading || projectIsLoading) {
      return <Preloader />;
    }

    return null;
  };

  renderUploadFileModal = () => {
    const {
      isUploading,
      hasUploadingFilesError,
      uploadFiles,
      resetUploadFiles,
    } = this.props;

    if (this.state.isUploadFileModalOpen) {
      return (
        <UploadFileModal
          hasError={hasUploadingFilesError}
          isLoading={isUploading}
          onClose={() => {
            resetUploadFiles();
            this.handleOnCloseModal();
          }}
          onUploadFiles={uploadFiles}
        />
      );
    }

    return null;
  };

  renderDeleteModal = () => {
    if (this.state.isDeleteModalOpen) {
      return (
        <DeleteModal
          isFetching={this.props.isUploading}
          onCloseRequest={this.handleCloseDeleteModal}
          onDelete={this.handleDeleteFiles}
        />
      );
    }

    return null;
  };

  renderEmptyView = () => {
    const {
      items,
      addNewFile,
      isLoading,
      searchTerm,
      projectIsLoading,
      project,
    } = this.props;

    if (
      !isLoading &&
      !projectIsLoading &&
      items.length === 0 &&
      !_isEmpty(project) &&
      !searchTerm
    ) {
      return <NoFiles addNewFile={addNewFile} />;
    }

    return null;
  };

  renderNoSearchResult() {
    const { items, isLoading, searchTerm, projectIsLoading } = this.props;

    if (
      !isLoading &&
      !projectIsLoading &&
      items.length === 0 &&
      searchTerm !== ''
    ) {
      const { items } = this.props;

      return (
        <UnitsSearchResult
          items={items}
          searchTerm={searchTerm}
          showAll={this.showAll}
          unitName="file"
        />
      );
    }

    return null;
  }

  renderList = () => {
    const {
      hasNameExceptions,
      updateFile,
      addFile,
      openFile,
      items,
      toggleFilesSelection,
      deleteFiles,
      exportFiles,
      deleteFile,
      exportFile,
      openFiles,
    } = this.props;

    if (items.length) {
      const getFiles = items.slice(
        (this.state.currentPage - 1) * PAGE_SIZE,
        (this.state.currentPage - 1) * PAGE_SIZE + PAGE_SIZE,
      );

      return (
        <List
          files={getFiles}
          hasNameExceptions={hasNameExceptions}
          onAddFile={addFile}
          onDeleteFile={deleteFile}
          onDeleteFiles={deleteFiles}
          onExportFile={exportFile}
          onExportFiles={exportFiles}
          onOpenFile={openFile}
          onOpenFiles={openFiles}
          onToggleFilesSelection={toggleFilesSelection}
          onUpdateFile={updateFile}
        />
      );
    }

    return null;
  };

  render() {
    const {
      modelName,
      items,
      searchTerm,
      count,
      sidebarIsOpen,
      addNewFile,
      exportFiles,
      updatedSearchTerm,
    } = this.props;

    return (
      <div data-test="files-pane" styleName="files">
        <FilesAndFoldersHeader
          addNewFile={addNewFile}
          deleteAll={this.handleOpenDeleteModal}
          exportAll={exportFiles}
          getItems={updatedSearchTerm}
          items={items}
          modelName={modelName}
          searchTerm={searchTerm}
          sidebarIsOpen={sidebarIsOpen}
          onOpenModal={this.handleOnOpenModal}
        />
        <div styleName="inner">
          {this.renderList()}
          {this.renderPreloader()}
          {this.renderUploadFileModal()}
          {this.renderEmptyView()}
          {this.renderNoSearchResult()}
          {this.renderDeleteModal()}
        </div>

        {/* Boolean(count) is a workaround to hide pagination in Resources - Files */}
        {/* Till we implement server side pagination we won't use count to calculate the pagesCount and the totalItems */}
        {Boolean(count) && (
          <SPagination
            currentPage={this.state.currentPage}
            initialPage={this.state.currentPage}
            pagesCount={Math.ceil(items.length / PAGE_SIZE)}
            size={PAGE_SIZE}
            totalItems={items.length}
            onPageChange={this.handlePageChange}
          />
        )}
      </div>
    );
  }
}
